import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

function clean(value) {
  return String(value ?? "").trim();
}

function list(value) {
  return Array.isArray(value) ? value : [];
}

function responsePayload(value) {
  return (
    value?.payload &&
    typeof value.payload === "object"
      ? value.payload
      : value
  ) || {};
}

function chaptersFromPlan(projectPlan) {
  return list(projectPlan?.bookMap?.items).filter(
    (item) => clean(item?.type) === "chapter"
  );
}

function chapterItemId(chapter) {
  return clean(
    chapter?.itemId ||
      chapter?.id
  );
}

function figureOccurrences(chapterDraft) {
  return list(chapterDraft?.blocks).filter(
    (block) =>
      clean(block?.type) === "figure" &&
      clean(block?.figureAssetId)
  );
}

function figureLabel(asset, isFR) {
  return (
    clean(asset?.caption) ||
    clean(asset?.alternativeText) ||
    clean(asset?.creatorOrSource) ||
    (isFR ? "Figure sans titre" : "Untitled Figure")
  );
}

function attentionItems(asset, occurrences) {
  const items = [];

  if (!clean(asset?.caption)) items.push("caption");
  if (!clean(asset?.alternativeText)) items.push("alt text");
  if (!clean(asset?.creatorOrSource)) items.push("source");
  if (!occurrences.length) items.push("placement");

  return items;
}

function PreviewImage({
  asset,
  onLoadFigureFile,
  isFR,
}) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fileUrl = clean(
    asset?.fileUrl ||
      asset?.publicUrl ||
      asset?.url
  );

  useEffect(() => {
    let alive = true;
    let objectUrl = "";

    setUrl("");
    setError("");

    if (!fileUrl) return () => {};

    if (
      /^https?:\/\//i.test(fileUrl) &&
      !fileUrl.includes("/api/")
    ) {
      setUrl(fileUrl);
      return () => {};
    }

    if (typeof onLoadFigureFile !== "function") {
      return () => {};
    }

    setLoading(true);

    Promise.resolve(onLoadFigureFile(fileUrl))
      .then((blob) => {
        if (
          !alive ||
          !blob ||
          typeof URL === "undefined" ||
          typeof URL.createObjectURL !== "function"
        ) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((loadError) => {
        if (!alive) return;

        setError(
          clean(loadError?.message) ||
            (isFR
              ? "Impossible de charger l’aperçu."
              : "Could not load the preview.")
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;

      if (
        objectUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL === "function"
      ) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileUrl, isFR, onLoadFigureFile]);

  if (loading) {
    return (
      <div style={{ padding: 28, textAlign: "center" }}>
        {isFR ? "Chargement de l’aperçu…" : "Loading preview…"}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 20, color: "#b91c1c" }}>
        {error}
      </div>
    );
  }

  if (!url) {
    return (
      <div style={{ padding: 20, color: "#64748b" }}>
        {isFR
          ? "Aucun fichier d’image disponible."
          : "No image file is available."}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={
        clean(asset?.alternativeText) ||
        clean(asset?.caption) ||
        (isFR ? "Aperçu de la Figure" : "Figure preview")
      }
      style={{
        width: "100%",
        maxHeight: 380,
        objectFit: "contain",
        borderRadius: 12,
        background: "#f8fafc",
      }}
    />
  );
}

export default function TextbookFiguresMedia({
  project,
  projectPlan,
  onLoadChapterDraft,
  onLoadFigureAssets,
  onLoadFigureFile,
  onUpdateFigure,
  onOpenChapter,
  onBackToWriting,
  canEdit = false,
  isFR = false,
}) {
  const chapters = useMemo(
    () => chaptersFromPlan(projectPlan),
    [projectPlan]
  );

  const projectId = clean(
    project?.id ||
      project?.projectId ||
      project?._id
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [previewRow, setPreviewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [savingId, setSavingId] = useState("");
  const [notice, setNotice] = useState("");

  async function loadAll() {
    if (!projectId || !chapters.length) {
      setRows([]);
      setLoading(false);
      setError("");
      return;
    }

    if (
      typeof onLoadChapterDraft !== "function" ||
      typeof onLoadFigureAssets !== "function"
    ) {
      setRows([]);
      setError(
        isFR
          ? "Les Figures ne peuvent pas être chargées."
          : "Figures cannot be loaded."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const chapterRows = await Promise.all(
        chapters.map(async (chapter, chapterIndex) => {
          const chapterId = chapterItemId(chapter);

          const [draftResult, figureResult] =
            await Promise.allSettled([
              onLoadChapterDraft(projectId, chapterId),
              onLoadFigureAssets(projectId, chapterId),
            ]);

          const draftPayload =
            draftResult.status === "fulfilled"
              ? responsePayload(draftResult.value)
              : {};

          const figurePayload =
            figureResult.status === "fulfilled"
              ? responsePayload(figureResult.value)
              : {};

          const chapterDraft =
            draftPayload?.chapterDraft || null;

          const assets =
            list(figurePayload?.figureAssets);

          const occurrences =
            figureOccurrences(chapterDraft);

          const occurrenceMap = new Map();

          occurrences.forEach((block) => {
            const id = clean(block?.figureAssetId);
            if (!id) return;

            const current = occurrenceMap.get(id) || [];
            current.push(block);
            occurrenceMap.set(id, current);
          });

          return assets
            .map((asset) => ({
              chapter,
              chapterId,
              chapterIndex,
              asset,
              occurrences:
                occurrenceMap.get(clean(asset?.id)) || [],
            }))
            .filter(
              (row) =>
                row.occurrences.length > 0
            );
        })
      );

      setRows(chapterRows.flat());
    } catch (loadError) {
      setRows([]);
      setError(
        clean(loadError?.message) ||
          (isFR
            ? "Impossible de charger les Figures du manuel."
            : "Could not load the textbook Figures.")
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projectPlan?.revision]);

  const grouped = useMemo(
    () =>
      chapters.map((chapter, chapterIndex) => ({
        chapter,
        chapterIndex,
        figures: rows.filter(
          (row) => row.chapterId === chapterItemId(chapter)
        ),
      })),
    [chapters, rows]
  );

  const attentionCount = rows.filter(
    (row) =>
      attentionItems(row.asset, row.occurrences).length > 0
  ).length;

  function beginEdit(row) {
    setNotice("");

    setEditRow({
      ...row,
      draft: {
        figureType: clean(row.asset?.figureType),
        caption: clean(row.asset?.caption),
        alternativeText: clean(row.asset?.alternativeText),
        creatorOrSource: clean(row.asset?.creatorOrSource),
        rightsOrLicense: clean(row.asset?.rightsOrLicense),
      },
    });
  }

  async function saveDetails() {
    if (
      !editRow ||
      typeof onUpdateFigure !== "function"
    ) {
      return;
    }

    const assetId = clean(editRow.asset?.id);
    const revision = Number(editRow.asset?.revision);

    if (!assetId || !Number.isInteger(revision)) {
      setNotice(
        isFR
          ? "Cette Figure ne peut pas être modifiée depuis cette vue."
          : "This Figure cannot be edited from this view."
      );
      return;
    }

    setSavingId(assetId);
    setNotice("");

    try {
      await onUpdateFigure(
        editRow.chapterId,
        assetId,
        editRow.draft,
        revision
      );

      setEditRow(null);
      await loadAll();

      setNotice(
        isFR
          ? "Détails de la Figure enregistrés."
          : "Figure details saved."
      );
    } catch (saveError) {
      setNotice(
        clean(saveError?.message) ||
          (isFR
            ? "Impossible d’enregistrer les détails de la Figure."
            : "Could not save the Figure details.")
      );
    } finally {
      setSavingId("");
    }
  }

  return (
    <section
      aria-label={isFR ? "Figures et médias" : "Figures & Media"}
      style={{ display: "grid", gap: 18 }}
    >
      <header
        className="ns-card"
        style={{
          padding: 20,
          borderRadius: 18,
          display: "grid",
          gap: 10,
        }}
      >
        <div
          style={{
            color: "#047857",
            fontSize: 12,
            fontWeight: 900,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          NextScenes Book Blueprint ·{" "}
          {isFR ? "Figures et médias" : "Figures & Media"}
        </div>

        <h2 style={{ margin: 0 }}>
          {isFR
            ? "Gérez les éléments visuels de votre manuel"
            : "Manage your textbook visuals"}
        </h2>

        <p
          style={{
            margin: 0,
            color: "#475569",
            lineHeight: 1.6,
          }}
        >
          {isFR
            ? "Les Figures restent liées aux chapitres où vous les avez placées. Cette vue vous aide à les retrouver, les vérifier et corriger leurs détails."
            : "Figures stay linked to the chapters where you placed them. This view helps you find them, check them, and correct their details."}
        </p>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          <span>{rows.length} Figure(s)</span>
          <span>·</span>
          <span>{chapters.length} chapter(s)</span>
          <span>·</span>
          <span>
            {attentionCount}{" "}
            {isFR ? "à vérifier" : "need attention"}
          </span>
        </div>
      </header>

      {notice ? (
        <div
          className="ns-card ns-card-soft"
          style={{ padding: 12, borderRadius: 12 }}
        >
          {notice}
        </div>
      ) : null}

      {loading ? (
        <div className="ns-card" style={{ padding: 22 }}>
          {isFR ? "Chargement des Figures…" : "Loading Figures…"}
        </div>
      ) : null}

      {error ? (
        <div
          className="ns-card"
          style={{ padding: 20, color: "#b91c1c" }}
        >
          {error}
        </div>
      ) : null}

      {!loading && !error && rows.length === 0 ? (
        <div
          className="ns-card"
          style={{
            padding: 22,
            display: "grid",
            gap: 10,
          }}
        >
          <strong>
            {isFR
              ? "Aucune Figure dans ce manuel pour le moment."
              : "No Figures in this textbook yet."}
          </strong>

          <span style={{ color: "#64748b" }}>
            {isFR
              ? "Ajoutez les Figures pendant la rédaction d’un chapitre. Elles apparaîtront ensuite ici automatiquement."
              : "Add Figures while writing a chapter. They will appear here automatically."}
          </span>

          {typeof onBackToWriting === "function" ? (
            <button
              className="ns-button ns-button-primary"
              type="button"
              onClick={onBackToWriting}
              style={{ justifySelf: "start" }}
            >
              {isFR ? "Rédiger les chapitres" : "Write Chapters"}
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && !error
        ? grouped.map(
            ({ chapter, chapterIndex, figures }) => {
              if (!figures.length) return null;

              return (
                <section
                  key={chapterItemId(chapter) || chapterIndex}
                  className="ns-card"
                  style={{
                    padding: 18,
                    borderRadius: 16,
                    display: "grid",
                    gap: 14,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 800,
                      }}
                    >
                      {isFR
                        ? `Chapitre ${chapterIndex + 1}`
                        : `Chapter ${chapterIndex + 1}`}
                    </div>

                    <h3 style={{ margin: "4px 0 0" }}>
                      {clean(chapter?.title) ||
                        (isFR
                          ? "Chapitre sans titre"
                          : "Untitled Chapter")}
                    </h3>
                  </div>

                  <div style={{ display: "grid", gap: 10 }}>
                    {figures.map((row) => {
                      const issues = attentionItems(
                        row.asset,
                        row.occurrences
                      );

                      return (
                        <article
                          key={clean(row.asset?.id)}
                          className="ns-card ns-card-soft"
                          style={{
                            padding: 14,
                            borderRadius: 14,
                            display: "grid",
                            gridTemplateColumns:
                              "minmax(0, 1fr) auto",
                            alignItems: "center",
                            gap: 14,
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <strong>
                              {figureLabel(row.asset, isFR)}
                            </strong>

                            <div
                              style={{
                                marginTop: 5,
                                color: "#64748b",
                                fontSize: 13,
                              }}
                            >
                              {row.occurrences.length > 0
                                ? isFR
                                  ? `${row.occurrences.length} emplacement(s) dans ce chapitre`
                                  : `${row.occurrences.length} placement(s) in this chapter`
                                : isFR
                                  ? "Aucun emplacement enregistré"
                                  : "No saved placement"}
                            </div>

                            <div
                              style={{
                                marginTop: 6,
                                color:
                                  issues.length > 0
                                    ? "#92400e"
                                    : "#166534",
                                fontSize: 12,
                                fontWeight: 800,
                              }}
                            >
                              {issues.length > 0
                                ? `${isFR ? "À vérifier" : "Check"}: ${issues.join(" · ")}`
                                : isFR
                                  ? "Prêt"
                                  : "Ready"}
                            </div>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 7,
                              justifyContent: "flex-end",
                            }}
                          >
                            <button
                              className="ns-button ns-button-outline"
                              type="button"
                              onClick={() => setPreviewRow(row)}
                            >
                              {isFR ? "Aperçu" : "Preview"}
                            </button>

                            {canEdit ? (
                              <button
                                className="ns-button ns-button-outline"
                                type="button"
                                onClick={() => beginEdit(row)}
                              >
                                {isFR
                                  ? "Modifier les détails"
                                  : "Edit details"}
                              </button>
                            ) : null}

                            <button
                              className="ns-button ns-button-primary"
                              type="button"
                              disabled={!row.occurrences.length}
                              onClick={() =>
                                onOpenChapter?.(row.chapterId)
                              }
                            >
                              {isFR
                                ? "Aller à l’emplacement"
                                : "Go to placement"}
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            }
          )
        : null}

      {previewRow ? (
        <div
          data-textbook-figures-media-preview="true"
          role="dialog"
          aria-modal="true"
          aria-label={isFR ? "Aperçu de la Figure" : "Figure preview"}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(15,23,42,0.58)",
            display: "grid",
            placeItems: "center",
            padding: 20,
          }}
          onClick={() => setPreviewRow(null)}
        >
          <div
            className="ns-card"
            style={{
              width: "min(760px, 96vw)",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: 18,
              borderRadius: 18,
              display: "grid",
              gap: 14,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <strong>
                {figureLabel(previewRow.asset, isFR)}
              </strong>

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => setPreviewRow(null)}
              >
                {isFR ? "Fermer" : "Close"}
              </button>
            </div>

            <PreviewImage
              asset={previewRow.asset}
              onLoadFigureFile={onLoadFigureFile}
              isFR={isFR}
            />
          </div>
        </div>
      ) : null}

      {editRow ? (
        <div
          data-textbook-figures-media-editor="true"
          role="dialog"
          aria-modal="true"
          aria-label={
            isFR
              ? "Modifier les détails de la Figure"
              : "Edit Figure details"
          }
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(15,23,42,0.58)",
            display: "grid",
            placeItems: "center",
            padding: 20,
          }}
          onClick={() => setEditRow(null)}
        >
          <div
            className="ns-card"
            style={{
              width: "min(720px, 96vw)",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: 20,
              borderRadius: 18,
              display: "grid",
              gap: 14,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 style={{ margin: 0 }}>
              {isFR ? "Détails de la Figure" : "Figure details"}
            </h3>

            {[
              ["figureType", isFR ? "Type de Figure" : "Figure type"],
              ["caption", isFR ? "Légende" : "Caption"],
              [
                "alternativeText",
                isFR ? "Texte alternatif" : "Alternative text",
              ],
              [
                "creatorOrSource",
                isFR ? "Créateur ou source" : "Creator or source",
              ],
              [
                "rightsOrLicense",
                isFR ? "Droits ou licence" : "Rights or licence",
              ],
            ].map(([field, label]) => (
              <label
                key={field}
                style={{
                  display: "grid",
                  gap: 6,
                  fontWeight: 700,
                }}
              >
                <span>{label}</span>
                <input
                  value={editRow.draft[field] || ""}
                  onChange={(event) =>
                    setEditRow((current) => ({
                      ...current,
                      draft: {
                        ...current.draft,
                        [field]: event.target.value,
                      },
                    }))
                  }
                />
              </label>
            ))}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={Boolean(savingId)}
                onClick={() => setEditRow(null)}
              >
                {isFR ? "Annuler" : "Cancel"}
              </button>

              <button
                className="ns-button ns-button-primary"
                type="button"
                disabled={Boolean(savingId)}
                aria-busy={
                  savingId === clean(editRow.asset?.id)
                }
                onClick={saveDetails}
              >
                {savingId
                  ? isFR
                    ? "Enregistrement…"
                    : "Saving…"
                  : isFR
                    ? "Enregistrer"
                    : "Save details"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {typeof onBackToWriting === "function" ? (
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onBackToWriting}
          style={{ justifySelf: "start" }}
        >
          {isFR ? "Retour à la rédaction" : "Back to Write Chapters"}
        </button>
      ) : null}
    </section>
  );
}
